import { createContext, useContext, useEffect, useState } from 'react';
import { ReactNode } from 'react';
import { getContrast, getLuminance, parseToHsl, hslToColorString, parseToRgb, readableColor, lighten } from 'polished';

const STORAGE_KEY = 'darkMode';
const DARK_CLASS = 'dark-mode';

export const BG = '#1a1d27';
export const SURFACE = '#21253a';
export const STRIPE = '#262b42';
export const RAISED = '#282d45';
export const BORDER = '#3a4162';
export const ROW_BORDER = 'rgba(255, 255, 255, 0.07)';
export const TEXT = '#e2e8f0';
export const LESS_MUTED = '#E2E8F0';
export const MUTED = '#878b9a';
export const INPUT = '#141726';
export const ACCENT = '#4a90d9';
export const SUCCESS = '#3d9449';
export const WARNING = '#d29922';
export const DANGER = '#f08080';
export const INFO = '#7ed0e0';

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface DarkModeColors {
  BG: string;
  SURFACE: string;
  STRIPE: string;
  RAISED: string;
  BORDER: string;
  ROW_BORDER: string;
  TEXT: string;
  MUTED: string;
  INPUT: string;
  ACCENT: string;
  SUCCESS: string;
  WARNING: string;
  DANGER: string;
  INFO: string;
}

export interface DarkModeContextValue {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  colors?: DarkModeColors;
}

// ── Static CSS (palette-only, no theme-derived colors) ───────────────────────

const DARK_CSS = `
  /* ── Palette as CSS custom properties ─────────────────────
     Exposes this file's own dark-mode palette constants as vars so other
     stylesheets (src/tailwind.css's --ui-* semantic tokens) can reference
     them with var(--dm-*) instead of duplicating the hex values. Always
     present (this <style> tag is injected once, regardless of dark-mode
     state) — src/tailwind.css scopes its own use of these under
     body.dark-mode/[data-theme='dark'] so they only take effect there. */
  :root {
    --dm-bg: ${BG};
    --dm-surface: ${SURFACE};
    --dm-raised: ${RAISED};
    --dm-border: ${BORDER};
    --dm-text: ${TEXT};
    --dm-less-muted: ${LESS_MUTED};
    --dm-muted: ${MUTED};
    --dm-input: ${INPUT};
  }

  /* ── Base ───────────────────────────────────────────────── */
  body.dark-mode {
    background-color: ${BG} !important;
    color: ${TEXT} !important;
    color-scheme: dark;

    .bg-white {
      background-color: ${BG} !important;
      color: ${TEXT} !important;
    }
  }

  /* ── Navbar ─────────────────────────────────────────────── */
  body.dark-mode nav.navbar {
    background: rgba(26, 29, 39, 0.82) !important;
    backdrop-filter: blur(10px) !important;
    -webkit-backdrop-filter: blur(10px) !important;
    border-bottom-color: rgba(255, 255, 255, 0.06) !important;
    box-shadow: 0 1px 20px rgba(0, 0, 0, 0.3) !important;
    color: ${TEXT} !important;
  }
  body.dark-mode nav.navbar .nav-icon,
  body.dark-mode nav.navbar .navbar-text.text-title,
  body.dark-mode nav.navbar .navbar-help-text {
    color: ${TEXT} !important;
  }
  body.dark-mode nav.navbar a.nav-link {
    color: ${MUTED} !important;
  }

  /* ── Traditional sidebar ─────────────────────────────────── */
  body.dark-mode .sidebar {
    background-color: ${SURFACE} !important;
  }
  body.dark-mode .sidebar > .nav.nav-tabs > .nav-item > .nav-link {
    border-color: ${SURFACE} !important;
    color: ${MUTED} !important;
  }
  body.dark-mode .sidebar > .nav.nav-tabs > .nav-item > .nav-link.active {
    background-color: ${BG} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .tab-content {
    background-color: ${BG} !important;
  }

  /* ── MUI sidebar ─────────────────────────────────────────── */
  body.dark-mode .mui-main-content {
    background: ${BG} !important;
  }
  body.dark-mode .mui-main-content .navbar {
    background: rgba(26, 29, 39, 0.82) !important;
  }
  body.dark-mode .mui-sidebar {
    border-right-color: rgba(255, 255, 255, 0.06) !important;
    box-shadow: 2px 0 28px rgba(0, 0, 0, 0.28) !important;
  }
  body.dark-mode .mui-sidebar::before {
    background: linear-gradient(180deg, rgba(33, 37, 58, 0.82) 0%, rgba(26, 29, 39, 0.78) 100%) !important;
    backdrop-filter: blur(14px) !important;
    -webkit-backdrop-filter: blur(14px) !important;
  }
  body.dark-mode .mui-user-dropdown .card.pc-user-card {
    background: ${RAISED} !important;
  }
  body.dark-mode .mui-toggle-btn {
    color: ${MUTED} !important;
  }
  body.dark-mode .mui-toggle-btn:hover {
    background-color: ${BORDER} !important;
  }

  /* ── Sidebar footer ─────────────────────────────────────────── */
  body.dark-mode .mui-sidebar-footer {
    border-top-color: ${BORDER} !important;
  }
  body.dark-mode .mui-sidebar-user-widget:hover {
    background: rgba(255, 255, 255, 0.06) !important;
  }
  body.dark-mode .mui-sidebar-user-name {
    color: ${TEXT} !important;
  }
  body.dark-mode .mui-sidebar-user-role {
    color: ${MUTED} !important;
  }
  body.dark-mode .mui-sidebar-footer-action-btn {
    color: ${MUTED} !important;
  }
  body.dark-mode .mui-sidebar-footer-action-btn:hover {
    background: rgba(255, 255, 255, 0.06) !important;
  }
  body.dark-mode .mui-sidebar-footer-action-btn.text-danger {
    color: #f87171 !important;
  }
  body.dark-mode #sidebar-logout-btn {
    color: #f87171 !important;
  }
  body.dark-mode #sidebar-logout-btn:hover {
    background: rgba(248, 113, 113, 0.12) !important;
  }

  /* ── Sidebar caption divider ────────────────────────────────── */
  body.dark-mode .mui-sidebar.mui-collapsed > .mui-sidebar-menu > .pc-item.pc-caption {
    border-top-color: ${BORDER} !important;
  }

  /* ── Sidebar skeleton ───────────────────────────────────────── */
  body.dark-mode .skeleton-circle,
  body.dark-mode .skeleton-line {
    background: rgba(255, 255, 255, 0.1) !important;
  }

  /* ── Mobile footer chevron ──────────────────────────────────── */
  body.dark-mode .mui-sidebar-chevron {
    color: ${MUTED} !important;
  }
  body.dark-mode .mui-sidebar-footer-mobile {
    border-bottom-color: ${BORDER} !important;
  }
  body.dark-mode .mui-sidebar-footer-toggles {
    background: rgba(255, 255, 255, 0.04) !important;
  }
  body.dark-mode .mui-sidebar-mobile-links {
    border-top-color: ${BORDER} !important;
  }

  /* ── Sidebar scrollbar ──────────────────────────────────────── */
  body.dark-mode .mui-sidebar-menu {
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent !important;
  }
  body.dark-mode .mui-sidebar-menu::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15) !important;
  }
  body.dark-mode .mui-sidebar-menu::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.28) !important;
  }
  body.dark-mode .mui-text {
    color: ${MUTED} !important;
  }
  body.dark-mode .mui-menu-item {
    color: ${MUTED} !important;
    &:hover {
      .mui-icon,
      .mui-text {
        color: ${LESS_MUTED} !important;
      }
    }
    &.active {
      color: ${ACCENT} !important;

      &:after {
        background-color: ${ACCENT} !important;
      }

      .mui-text {
        color: ${ACCENT} !important;
      }
    }
  }
  body.dark-mode .pc-caption {
    color: ${MUTED} !important;
  }
  body.dark-mode .card.pc-user-card .pc-user-links button,
  body.dark-mode .pc-user-item {
    color: ${MUTED} !important;
  }
  body.dark-mode .card.pc-user-card .pc-user-links a:hover,
  body.dark-mode .card.pc-user-card .pc-user-links a:focus {
    color: ${TEXT} !important;
  }

  /* ── Cards ──────────────────────────────────────────────── */
  body.dark-mode .card,
  body.dark-mode .card-body {
    background-color: ${SURFACE} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .card-header,
  body.dark-mode .card-footer {
    background-color: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .tab-content .card-body {
    background: ${SURFACE} !important;
  }
  body.dark-mode .card-footer-container {
    background-color: ${SURFACE} !important;
  }
  body.dark-mode .uweentity-title {
    background-color: ${SURFACE} !important;
  }

  /* ── Dropdowns ──────────────────────────────────────────── */
  body.dark-mode .dropdown-menu,
  body.dark-mode .user-dropdown-menu,
  body.dark-mode .notification-dropdown-menu,
  body.dark-mode .medium-panel,
  body.dark-mode .medium-panel-user {
    background-color: ${SURFACE} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .dropdown-item {
    color: ${TEXT} !important;
  }
  body.dark-mode .dropdown-item:hover,
  body.dark-mode .dropdown-item:focus {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .dropdown-divider,
  body.dark-mode hr {
    border-color: ${BORDER} !important;
  }
  body.dark-mode .dropdown-organization-selected .dropdown-divider {
    border-top-color: ${BORDER} !important;
  }
  body.dark-mode .dropdownOrganizationSelected {
    background: ${RAISED} !important;
    color: ${MUTED} !important;
    border-color: ${BORDER} !important;
  }

  /* ── Form errors dropdown ────────────────────────────────── */
  body.dark-mode .form-errors-dropdown {
    background: ${SURFACE} !important;
  }
  body.dark-mode .form-errors-dropdown .error-item-msg {
    color: ${TEXT} !important;
  }

  /* ── Notification panel ──────────────────────────────────── */
  body.dark-mode .notification-panel-header {
    background: ${SURFACE} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .notification-item {
    border-bottom-color: ${BORDER} !important;
  }
  body.dark-mode .notification-item:hover {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .notification-item.unread {
    background-color: ${RAISED} !important;
  }

  /* ── Modals ─────────────────────────────────────────────── */
  body.dark-mode .modal-content {
    background-color: ${SURFACE} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .modal-header,
  body.dark-mode .modal-footer {
    border-color: ${BORDER} !important;
  }
  body.dark-mode .modal-resize-grip::after {
    border-right-color: ${MUTED} !important;
    border-bottom-color: ${MUTED} !important;
  }

  /* ── Tables ─────────────────────────────────────────────── */
  body.dark-mode table {
    background-color: ${BG} !important;
  }
  body.dark-mode .table {
    color: ${TEXT} !important;
    border-color: ${ROW_BORDER} !important;
  }
  body.dark-mode .table > thead {
    background-color: ${RAISED} !important;
    color: ${TEXT} !important;
    border-color: ${ROW_BORDER} !important;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  body.dark-mode .table > tbody > tr:nth-of-type(even) {
    background-color: ${STRIPE} !important;
  }
  body.dark-mode .table > tbody > tr:nth-of-type(odd) {
    background-color: ${SURFACE} !important;
  }
  body.dark-mode .table > tbody > tr:hover {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .table > tbody > tr:hover > * {
    color: ${TEXT} !important;
    --bs-table-color: ${TEXT};
  }
  body.dark-mode .table > tbody > tr > * {
    border-color: ${ROW_BORDER} !important;
  }
  body.dark-mode .rjsf-object-table thead tr {
    outline-color: ${ROW_BORDER} !important;
  }
  body.dark-mode .worktray-table-container .table > thead,
  body.dark-mode .worktray-flat-scroll .table > thead {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .loader-container {
    background-color: ${SURFACE} !important;
  }

  /* ── Worktray / workpage ─────────────────────────────────── */
  body.dark-mode .workpage {
    background-color: ${BG} !important;
  }
  body.dark-mode .worktray > .card:not(.form-errors) {
    background-color: ${SURFACE} !important;
  }

  /* ── Pagination ─────────────────────────────────────────── */
  body.dark-mode nav[aria-label='pagination'] {
    background-color: ${SURFACE} !important;
  }
  body.dark-mode .page-link {
    background-color: ${SURFACE} !important;
    color: ${TEXT} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .page-item.disabled .page-link {
    background-color: ${BG} !important;
    color: ${MUTED} !important;
    border-color: ${BORDER} !important;
  }

  /* ── Forms & inputs ─────────────────────────────────────── */
  body.dark-mode .control-label {
    color: ${TEXT} !important;
  }
      body.dark-mode input[type='text'][id^='react-select']:not(.ghost-input),
  body.dark-mode .react-select__input {
    background-color: transparent !important;
    border-width: 0 !important;
  }
  body.dark-mode .form-control:not(.ghost-input),
  body.dark-mode .form-select:not(.ghost-input),
  body.dark-mode input.form-control:not(.ghost-input),
  body.dark-mode textarea.form-control:not(.ghost-input),
  body.dark-mode select.form-control:not(.ghost-input),
  body.dark-mode input[type='text']:not(.ghost-input),
  body.dark-mode input[type='email']:not(.ghost-input),
  body.dark-mode input[type='password']:not(.ghost-input),
  body.dark-mode input[type='number']:not(.ghost-input),
  body.dark-mode input[type='search']:not(.ghost-input),
  body.dark-mode input[type='date']:not(.ghost-input),
  body.dark-mode input[type='datetime-local']:not(.ghost-input),
  body.dark-mode textarea:not(.ghost-input),
  body.dark-mode select:not(.ghost-input) {
    background-color: ${INPUT};
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode input[type='text'][id^='react-select']:not(.ghost-input),
  body.dark-mode .react-select__input {
    background-color: transparent !important;
    border-width: 0 !important;
  }
  body.dark-mode .form-control:focus,
  body.dark-mode .form-select:focus,
  body.dark-mode input:focus,
  body.dark-mode textarea:focus,
  body.dark-mode select:focus {
    background-color: ${INPUT} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .form-control::placeholder,
  body.dark-mode input::placeholder,
  body.dark-mode textarea::placeholder {
    color: ${MUTED} !important;
  }
  body.dark-mode .form-control:disabled,
  body.dark-mode .form-control[readonly],
  body.dark-mode input.form-control[readonly],
  body.dark-mode textarea.form-control[readonly],
  body.dark-mode input:disabled,
  body.dark-mode textarea:disabled {
    background-color: ${RAISED} !important;
    color: ${MUTED} !important;
  }
  body.dark-mode select.form-control:hover {
    background-color: ${RAISED} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .input-group-text {
    background-color: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${MUTED} !important;
  }
  body.dark-mode .ghost-input,
  body.dark-mode .input-group:has(.ghost-input),
  body.dark-mode .input-group-ghost {
    border-bottom-color: rgba(226, 228, 233,0.25) !important;
  }
  body.dark-mode .ghost-input:focus,
  body.dark-mode .input-group:has(.ghost-input:focus),
  body.dark-mode .input-group-ghost:focus-within {
    border-bottom-color: rgba(226, 228, 233,0.7) !important;
  }
  body.dark-mode .ghost-input::placeholder {
    color: rgba(226, 228, 233,0.35) !important;
  }
  body.dark-mode .input-group-ghost.array-filter-input-group {
    border-bottom-color: transparent !important;
  }
  body.dark-mode .input-group-ghost.array-filter-input-group:focus-within,
  body.dark-mode .input-group-ghost.array-filter-input-group:hover,
  body.dark-mode .input-group-ghost.array-filter-input-group.has-value {
    border-bottom-color: rgba(226, 228, 233,0.7) !important;
  }

  /* ── Form tabs ──────────────────────────────────────────── */
  body.dark-mode .form-tabs.sticky-tab-nav,
  body.dark-mode .form-grouped-tabs.sticky-tab-nav {
    background-color: ${SURFACE} !important;
  }
  body.dark-mode .nav-item.add-button {
    background-color: ${SURFACE} !important;
  }
  body.dark-mode .object-field-template-footer:not(.card-footer) {
    border-top-color: ${BORDER} !important;
  }

  /* ── Nav tabs ───────────────────────────────────────────── */
  body.dark-mode .nav-tabs {
    border-bottom-color: ${BORDER} !important;
  }
  body.dark-mode .nav-tabs .nav-link {
    color: ${MUTED} !important;
  }
  body.dark-mode .nav-tabs .nav-link:hover {
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .nav-tabs .nav-link.active {
    background-color: ${SURFACE} !important;
    border-color: ${BORDER} ${BORDER} ${SURFACE} !important;
    color: ${ACCENT} !important;
  }

  /* ── Grouped tabs (pill-style) ───────────────────────────── */
  body.dark-mode .form-grouped-tabs.nav .nav-link:not(.active) {
    color: ${MUTED} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .form-grouped-tabs.nav .nav-link:hover:not(.active) {
    color: ${TEXT} !important;
    background-color: ${RAISED} !important;
  }
  body.dark-mode .form-grouped-tabs.nav .nav-item.dropdown:not(.active) .dropdown-toggle {
    color: ${MUTED} !important;
  }
  body.dark-mode .form-grouped-tabs.nav .nav-item.dropdown:not(.active) .dropdown-toggle:hover {
    color: ${TEXT} !important;
    background-color: ${RAISED} !important;
  }

  /* ── React-Select / Autocomplete ─────────────────────────── */
  body.dark-mode .react-select__control,
  body.dark-mode .autocomplete__control {
    background-color: ${INPUT} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .react-select__menu,
  body.dark-mode .autocomplete__menu {
    background-color: ${SURFACE} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .react-select__option,
  body.dark-mode .autocomplete__option {
    background-color: ${SURFACE} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .react-select__option--is-focused,
  body.dark-mode .autocomplete__option--is-focused {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .react-select__single-value,
  body.dark-mode .react-select__input-container,
  body.dark-mode .autocomplete__single-value,
  body.dark-mode .autocomplete__input-container {
    color: ${TEXT} !important;
  }
  body.dark-mode .react-select__placeholder,
  body.dark-mode .autocomplete__placeholder {
    color: ${MUTED} !important;
    padding-left: 4px;
  }
  body.dark-mode .react-select__multi-value,
  body.dark-mode .autocomplete__multi-value {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .react-select__multi-value__label,
  body.dark-mode .autocomplete__multi-value__label {
    color: ${TEXT} !important;
  }

  /* ── React-Datepicker ────────────────────────────────────────
     Popup is portalled to <body>, so these apply to every DatePicker.
     Selected-day colors live in buildSemanticCSS (theme primary). */
  body.dark-mode .react-datepicker {
    background-color: ${SURFACE} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .react-datepicker__header {
    background-color: ${RAISED} !important;
    border-bottom-color: ${BORDER} !important;
  }
  body.dark-mode .react-datepicker__current-month,
  body.dark-mode .react-datepicker__day-name,
  body.dark-mode .react-datepicker-time__header,
  body.dark-mode .react-datepicker-year-header,
  body.dark-mode .react-datepicker__year-read-view,
  body.dark-mode .react-datepicker__month-read-view,
  body.dark-mode .react-datepicker__month-year-read-view,
  body.dark-mode .react-datepicker__day,
  body.dark-mode .react-datepicker__month-text,
  body.dark-mode .react-datepicker__quarter-text,
  body.dark-mode .react-datepicker__year-text,
  body.dark-mode .react-datepicker__time-list-item {
    color: ${TEXT} !important;
  }
  body.dark-mode .react-datepicker__day:not([aria-disabled='true']):hover,
  body.dark-mode .react-datepicker__month-text:not([aria-disabled='true']):hover,
  body.dark-mode .react-datepicker__quarter-text:not([aria-disabled='true']):hover,
  body.dark-mode .react-datepicker__year-text:not([aria-disabled='true']):hover,
  body.dark-mode .react-datepicker__time-list-item:hover {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--selected),
  body.dark-mode .react-datepicker__month-text--keyboard-selected,
  body.dark-mode .react-datepicker__quarter-text--keyboard-selected,
  body.dark-mode .react-datepicker__year-text--keyboard-selected {
    background-color: ${RAISED} !important;
    outline: 1px solid ${BORDER};
  }
  body.dark-mode .react-datepicker__day--disabled,
  body.dark-mode .react-datepicker__month-text--disabled,
  body.dark-mode .react-datepicker__quarter-text--disabled,
  body.dark-mode .react-datepicker__year-text--disabled,
  body.dark-mode .react-datepicker__time-list-item--disabled {
    color: ${MUTED} !important;
    opacity: 0.45;
  }
  body.dark-mode .react-datepicker__navigation-icon::before,
  body.dark-mode .react-datepicker__year-read-view--down-arrow,
  body.dark-mode .react-datepicker__month-read-view--down-arrow,
  body.dark-mode .react-datepicker__month-year-read-view--down-arrow {
    border-color: ${MUTED} !important;
  }
  body.dark-mode .react-datepicker__navigation:hover *::before {
    border-color: ${TEXT} !important;
  }
  body.dark-mode .react-datepicker__year-dropdown,
  body.dark-mode .react-datepicker__month-dropdown,
  body.dark-mode .react-datepicker__month-year-dropdown,
  body.dark-mode .react-datepicker__time-container,
  body.dark-mode .react-datepicker__time,
  body.dark-mode .react-datepicker__time-box {
    background-color: ${SURFACE} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .react-datepicker__year-option:hover,
  body.dark-mode .react-datepicker__month-option:hover,
  body.dark-mode .react-datepicker__month-year-option:hover {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .react-datepicker__today-button {
    background-color: ${RAISED} !important;
    border-top-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .react-datepicker__calendar-icon {
    color: ${MUTED};
  }

  /* ── Ant Design (antd) Select / InputNumber / AutoComplete ─
     antd's className props land on the outer wrapper, not the inner
     elements that actually carry background/text (.ant-select-selector,
     .ant-input-number-input) — Tailwind classes passed via className
     can't reach them. The dropdown/menu is also portaled to <body>, so
     component-scoped styling could never reach it anyway. Global,
     class-based overrides (same approach as .react-select__* above)
     are the only thing that works for both. */
  body.dark-mode .ant-select-selector,
  body.dark-mode .ant-input-number {
    background-color: ${INPUT} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .ant-input-number-input {
    background-color: transparent !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .ant-select-selection-search-input {
    color: ${TEXT} !important;
  }
  body.dark-mode .ant-select-selection-placeholder,
  body.dark-mode .ant-input-number-input::placeholder {
    color: ${MUTED} !important;
  }
  body.dark-mode .ant-select-selection-item,
  body.dark-mode .ant-select-arrow,
  body.dark-mode .ant-select-clear {
    color: ${TEXT} !important;
  }
  body.dark-mode .ant-select-clear {
    background-color: ${INPUT} !important;
  }
  body.dark-mode .ant-select-multiple .ant-select-selection-item {
    background-color: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .ant-select-selection-item-remove {
    color: ${MUTED} !important;
  }
  body.dark-mode .ant-input-number-handler-wrap {
    background-color: ${INPUT} !important;
    border-left-color: ${BORDER} !important;
  }
  body.dark-mode .ant-input-number-handler {
    border-color: ${BORDER} !important;
  }
  body.dark-mode .ant-input-number-handler-up-inner,
  body.dark-mode .ant-input-number-handler-down-inner {
    color: ${MUTED} !important;
  }
  body.dark-mode .ant-select-disabled .ant-select-selector,
  body.dark-mode .ant-input-number-disabled {
    background-color: ${RAISED} !important;
    color: ${MUTED} !important;
  }
  /* Dropdown/menu popups are portaled to <body>, outside any component's
     own subtree, so they need their own top-level rules. */
  body.dark-mode .ant-select-dropdown {
    background-color: ${SURFACE} !important;
    border: 1px solid ${BORDER};
  }
  body.dark-mode .ant-select-item {
    color: ${TEXT} !important;
  }
  body.dark-mode .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background-color: ${RAISED} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .ant-select-item-option-disabled {
    color: ${MUTED} !important;
  }
  body.dark-mode .ant-empty-description {
    color: ${MUTED} !important;
  }

  /* ── Accordion (AccordionItem) ──────────────────────────────
     .bg-light is a static bootstrap-compat utility (always light gray)
     with no dark-mode counterpart of its own — AccordionItem's header
     hardcodes it, so it needs the same body-class patch already used
     for .bg-white/.form-control above. */
  body.dark-mode .accordion-header {
    background-color: ${SURFACE} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .accordion-header:hover {
    background-color: ${RAISED} !important;
  }

  /* ── Loader ─────────────────────────────────────────────── */
  body.dark-mode .loader-backdrop {
    background-color: rgba(26, 29, 39, 0.85) !important;
  }
  body.dark-mode .loader-text {
    color: ${MUTED} !important;
  }

  /* ── Dataremark cards ────────────────────────────────────── */
  body.dark-mode .dataremark-item-card {
    background: ${SURFACE} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .dataremark-item-card .card-header {
    background: ${RAISED} !important;
    border-bottom-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .dataremark-item-card h6 {
    color: ${TEXT} !important;
  }
  body.dark-mode .dataremark-details {
    background: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }

  /* ── Dataremark accordion items ──────────────────────────── */
  body.dark-mode .dataremarks-grid-partial > div {
    border-top-color: ${BORDER} !important;
    border-right-color: ${BORDER} !important;
    border-bottom-color: ${BORDER} !important;
    background-color: ${SURFACE} !important;
    color: ${TEXT} !important;
    border-radius: 4px;
    &.active {
      border-top-color: ${MUTED} !important;
      border-right-color: ${MUTED} !important;
      border-bottom-color: ${MUTED} !important;
    }
  }
  body.dark-mode .accordion-header {
    background-color: ${RAISED} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .dataremarks-grid-partial .accordion-header {
    background-color: ${RAISED} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .dataremarks-grid-partial .accordion-header:hover {
    background-color: rgba(255, 255, 255, 0.06) !important;
  }
  body.dark-mode .dataremarks-grid-partial .bg-light {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .dataremarks-grid-partial .border-top {
    border-top-color: ${BORDER} !important;
  }

  /* ── Sidepanel workspace ─────────────────────────────────── */
  body.dark-mode .sp-side-panel,
  body.dark-mode .sp-bottom-panel,
  body.dark-mode .sp-float-modal {
    background: ${SURFACE} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .sp-side-tabs {
    background: ${BG} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .sp-side-tab {
    border-bottom-color: ${BORDER} !important;
  }
  body.dark-mode .sp-side-tab.tab-primary {
    color: ${MUTED} !important;
    background: ${BG} !important;
    &.active-primary {
      color: ${TEXT} !important;
      background: ${RAISED} !important;
    }
  }
  body.dark-mode .sp-side-tab.tab-warning:not(.active-warning),
  body.dark-mode .sp-side-tab.tab-danger:not(.active-danger) {
    color: ${MUTED} !important;
  }
  body.dark-mode .sp-side-tab.tab-primary:hover {
    background: ${RAISED} !important;
  }
  body.dark-mode .sp-v-resizer,
  body.dark-mode .sp-h-resizer {
    background: ${BORDER} !important;
  }
  body.dark-mode .sp-resize-grip::after {
    border-right-color: ${MUTED} !important;
    border-bottom-color: ${MUTED} !important;
  }

  /* ── Striped list (uweentity sidepanel) ──────────────────── */
  body.dark-mode .uweentity-form-full-expanded-sidepanel ul.striped-list > li {
    border-bottom-color: ${BORDER} !important;
  }
  body.dark-mode .uweentity-form-full-expanded-sidepanel ul.striped-list > li:nth-of-type(odd) {
    background-color: ${RAISED} !important;
  }

  /* ── Preview ─────────────────────────────────────────────── */
  body.dark-mode .preview-container {
    background-color: ${BG} !important;
  }
  body.dark-mode .preview-content {
    background-color: ${SURFACE} !important;
    border-color: ${BORDER} !important;
  }

  /* ── Welcome box ─────────────────────────────────────────── */
  body.dark-mode .welcome-box {
    background-color: ${SURFACE} !important;
    color: ${TEXT} !important;
  }

 /* ── Misc utilities ─────────────────────────────────────── */
  body.dark-mode .text-muted {
    color: ${MUTED} !important;
  }

  /* ── Disabled buttons ───────────────────────────────────── */
  body.dark-mode .btn:disabled,
  body.dark-mode .btn.disabled,
  body.dark-mode button:disabled {
    background-color: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${MUTED} !important;
    opacity: 0.55 !important;
  }

  body.dark-mode .btn-light {
    background-color: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .btn-secondary {
    color: ${TEXT} !important;
    border-color: ${TEXT} !important;
    background-color: transparent !important;
  }
  body.dark-mode .btn-outline-secondary {
    color: ${MUTED} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .btn-outline-secondary:hover {
    background-color: ${RAISED} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode fieldset {
    border-color: ${BORDER} !important;
  }
  body.dark-mode legend {
    color: ${TEXT} !important;
  }
  body.dark-mode ::-webkit-scrollbar-thumb {
    background-color: ${BORDER} !important;
  }
  body.dark-mode ::-webkit-scrollbar-thumb:hover {
    background-color: ${MUTED} !important;
  }

  /* ── CollapsableTreeSelect ───────────────────────────────── */
  body.dark-mode .tree-select-menu {
    background: ${SURFACE} !important;
    border-color: ${BORDER} !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5) !important;
  }
  body.dark-mode .tree-select-menu .tree-select-search {
    background: ${SURFACE} !important;
    border-bottom-color: ${BORDER} !important;
  }
  body.dark-mode .tree-select-menu .tree-select-empty {
    color: ${MUTED} !important;
  }
  body.dark-mode .tree-select-menu .tree-node {
    border-bottom-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .tree-select-menu .tree-node.tree-node-selectable:hover {
    background: ${RAISED} !important;
  }
  body.dark-mode .tree-select-menu .tree-node.tree-node-disabled .tree-node-label {
    color: ${MUTED} !important;
  }
  body.dark-mode .tree-select-menu .tree-node .tree-node-breadcrumb {
    color: ${MUTED} !important;
  }
  body.dark-mode .tree-select-menu .tree-node .tree-node-unselectable-badge {
    color: ${BORDER} !important;
  }
  body.dark-mode .tree-select-menu .tree-node .tree-node-toggle-btn {
    color: ${MUTED} !important;
  }
  body.dark-mode .tree-select-menu .tree-node .tree-node-toggle-btn:hover,
  body.dark-mode .tree-select-menu .tree-node .tree-node-toggle-btn:focus {
    background: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .tree-select-toggle.disabled {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .tree-select-path-hint {
    color: ${MUTED} !important;
  }

  /* ── UWEEntityNotes ─────────────────────────────────────── */
  body.dark-mode .note-compose {
    background: ${RAISED} !important;
    border-bottom-color: ${BORDER} !important;
  }
  body.dark-mode .note-compose .note-compose-input {
    background: ${INPUT} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .note-card {
    border-bottom-color: ${BORDER} !important;
  }
  body.dark-mode .note-card .note-user {
    color: ${TEXT} !important;
  }
  body.dark-mode .note-card .note-date {
    color: ${MUTED} !important;
  }
  body.dark-mode .note-card .note-text {
    color: ${TEXT} !important;
  }

  /* ── UWEEntityHistory ────────────────────────────────────── */
  body.dark-mode .hist-dot {
    border-color: ${BG} !important;
  }
  body.dark-mode .hist-line {
    background: ${BORDER} !important;
  }
  body.dark-mode .hist-date {
    color: ${MUTED} !important;
  }
  body.dark-mode .hist-action {
    color: ${TEXT} !important;
  }
  body.dark-mode .hist-option {
    color: ${MUTED} !important;
  }
  body.dark-mode .hist-reason {
    background: ${RAISED} !important;
    border-left-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }

  /* SearchBar */
  body.dark-mode .search-bar.card .card-body {
    background-color: ${BG} !important;
  }

  /* ── Confirmation modal ─────────────────────────────────── */
  body.dark-mode .textModalConfirm,
  body.dark-mode .textBottomModalConfirm {
    color: ${TEXT} !important;
  }

  /* ── Item details ───────────────────────────────────────── */
  body.dark-mode div.item-details .sectionTitle {
    color: ${TEXT} !important;
  }
  body.dark-mode div.item-details .sectionTitle h1 {
    color: ${TEXT} !important;
    background: ${SURFACE} !important;
  }
  body.dark-mode div.item-details .sectionTitle::after {
    background: ${BORDER} !important;
  }

  /* ── Entity editor ──────────────────────────────────────── */
  body.dark-mode .entity-editor .editor-section .schema-element-palette .schema-element {
    background-color: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .entity-editor .editor-section .schema-node:hover {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .entity-editor .editor-section .metadata-row:nth-of-type(even) {
    background-color: ${SURFACE} !important;
  }
  body.dark-mode .entity-editor .editor-section .metadata-row:hover {
    background-color: ${RAISED} !important;
  }
  body.dark-mode .drop-target {
    background: ${RAISED} !important;
    border-color: ${BORDER} !important;
  }

  /* ── Tooltips ───────────────────────────────────────────── */
  body.dark-mode .tooltip-inner {
    background-color: ${RAISED} !important;
    color: ${TEXT} !important;
    border-color: ${BORDER} !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4) !important;
  }
  body.dark-mode .bs-tooltip-top .arrow::before,
  body.dark-mode .bs-tooltip-auto[x-placement^='top'] .arrow::before {
    border-top-color: ${RAISED} !important;
  }
  body.dark-mode .bs-tooltip-right .arrow::before,
  body.dark-mode .bs-tooltip-auto[x-placement^='right'] .arrow::before {
    border-right-color: ${RAISED} !important;
  }
  body.dark-mode .bs-tooltip-bottom .arrow::before,
  body.dark-mode .bs-tooltip-auto[x-placement^='bottom'] .arrow::before {
    border-bottom-color: ${RAISED} !important;
  }
  body.dark-mode .bs-tooltip-left .arrow::before,
  body.dark-mode .bs-tooltip-auto[x-placement^='left'] .arrow::before {
    border-left-color: ${RAISED} !important;
  }
  body.dark-mode .white-tooltip-inner {
    background-color: ${RAISED} !important;
    color: ${TEXT} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .white-tooltip-arrow::before {
    border-top-color: ${BORDER} !important;
  }

  /* ── React Flow / workflow diagram ──────────────────────── */
  body.dark-mode .react-flow {
    --xy-background-color: ${BG};
    --xy-theme-hover: ${BORDER};
    --xy-theme-edge-hover: ${TEXT};
    --xy-theme-color-focus: ${RAISED};
    --xy-node-border-default: 1px solid ${BORDER};
    --xy-node-boxshadow-default: 0px 3.54px 4.55px 0px rgba(0,0,0,0.25), 0px 3.54px 4.55px 0px rgba(0,0,0,0.3);
    --xy-handle-background-color-default: ${SURFACE};
    --xy-handle-border-color-default: ${BORDER};
    --xy-edge-label-color-default: ${MUTED};
    --xy-node-background-color-default: ${SURFACE};
    --xy-node-color-default: ${TEXT};
    --xy-panel-background-color: ${SURFACE};
    --xy-controls-button-background-color-default: ${RAISED};
    --xy-controls-button-border-color-default: ${BORDER};
    --xy-controls-button-color-default: ${TEXT};
    --xy-controls-button-background-color-hover-default: ${BORDER};
    --xy-minimap-background-color-default: ${SURFACE};
    --xy-minimap-mask-background-color-default: rgba(26, 29, 39, 0.6);
    --xy-edge-stroke-default: ${MUTED};
    --xy-edge-stroke-selected-default: ${TEXT};
    --xy-selection-background-color-default: rgba(226, 228, 233, 0.08);
    --xy-selection-border-default: 1px dotted ${MUTED};
    background-color: ${BG};
    color: ${TEXT};
  }
  body.dark-mode .react-flow__background {
    background-color: ${BG} !important;
  }
  body.dark-mode .react-flow__panel {
    background-color: transparent !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .react-flow__controls {
    background-color: ${SURFACE} !important;
    border-color: ${BORDER} !important;
  }
  body.dark-mode .react-flow__controls-button {
    background-color: ${RAISED} !important;
    border-bottom-color: ${BORDER} !important;
    fill: ${TEXT} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .react-flow__controls-button:hover {
    background-color: ${BORDER} !important;
  }
  body.dark-mode .react-flow__minimap {
    background-color: ${SURFACE} !important;
    border: 1px solid ${BORDER} !important;
  }
  body.dark-mode .react-flow__edge-textbg {
    fill: ${BG} !important;
  }
  body.dark-mode .react-flow__edge-text {
    fill: ${MUTED} !important;
  }
  body.dark-mode .xy-theme__button,
  body.dark-mode .xy-theme__button-group > * {
    background-color: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .xy-theme__button:active,
  body.dark-mode .xy-theme__button.is-active,
  body.dark-mode .xy-theme__button-group > *:active,
  body.dark-mode .xy-theme__button-group > *.is-active {
    background-color: var(--color-primary) !important;
    color: #fff !important;
  }

  /* ── Workflow editor ────────────────────────────────────── */
  body.dark-mode ._workflowEditor .nav-link {
    color: ${TEXT} !important;
  }

  /* ── Org unit tree ───────────────────────────────────────── */
  body.dark-mode .organizational-unit-tree {
    background: ${SURFACE} !important;
  }
  body.dark-mode .organizational-unit-tree-node > foreignObject > div {
    border-color: ${BORDER} !important;
    background-color: ${SURFACE} !important;
  }
  body.dark-mode .organizational-unit-tree-node > foreignObject > div > .body,
  body.dark-mode .organizational-unit-tree-node > foreignObject > div > .buttons {
    background: ${RAISED} !important;
  }

  body.dark-mode .btn-clear {
    color: ${MUTED} !important;
  }

  /* ── AI chat modal (chatscope) ───────────────────────────── */
  body.dark-mode .cs-chat-container,
  body.dark-mode .cs-message-list {
    background: ${SURFACE} !important;
  }
  body.dark-mode .cs-message--incoming .cs-message__content {
    background-color: ${RAISED} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .cs-message--outgoing .cs-message__content {
    background-color: #1e3a5a !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .cs-message-input__content-editor-wrapper {
    background-color: ${INPUT} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .cs-message-input__content-editor-wrapper > .cs-message-input__content-editor-container,
  body.dark-mode .cs-message-input__content-editor-wrapper > .cs-message-input__content-editor-container > .cs-message-input__content-editor {
    background-color: ${INPUT} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .cs-typing-indicator {
    background: ${SURFACE} !important;
    color: ${MUTED} !important;
  }

  /* ── Code tags ──────────────────────────────────────────── */
  body.dark-mode code {
    background-color: ${RAISED} !important;
    color: #e8a87c !important;
    border-radius: 3px;
    padding: 0.1em 0.3em;
  }
  body.dark-mode pre code {
    background-color: transparent !important;
    padding: 0;
  }
  body.dark-mode pre {
    background-color: ${RAISED} !important;
    border-color: ${BORDER} !important;
    color: ${TEXT} !important;
  }

  /* ── Switch toggle ───────────────────────────────────────── */
  body.dark-mode .switch {
    --slider-bg: ${BORDER};
    --slider-button: ${TEXT};
    --slider-border-readonly-bg: ${BORDER};
    --slider-button-readonly-bg: ${MUTED};
    --slider-checked-readonly-bg: ${SURFACE};
  }
  /* apply the readonly knob colour that the base CSS defines but never uses */
  body.dark-mode .switch.readonly > .slider:before {
    background-color: ${MUTED} !important;
  }
  /* checked + readonly: faint blue tint so the value is still readable */
  body.dark-mode .switch.readonly > input:checked + .slider {
    background-color: #1e3a5a !important;
    border-color: #2a5080 !important;
  }
  body.dark-mode .switch.readonly {
    cursor: not-allowed;
    opacity: 0.75;
  }

  /* ── Empty state SVG icons ───────────────────────────────────── */
  /* Target the decorative background cloud/blob shapes by their fill values.
     CSS presentation-attribute override: CSS fill > SVG fill attribute.
     Add className="empty-state-svg" (applied automatically by each component)
     to any wrapper or pass it via the className prop. */
  body.dark-mode .empty-state-svg [fill="#F3F7FF"] {
    fill: ${RAISED} !important;
  }
  body.dark-mode .empty-state-svg [fill="#E8F0FE"] {
    fill: ${SURFACE} !important;
  }

  /* ── Error Fallback ─────────────────────────────────────── */
  body.dark-mode .error-fallback-stack {
    background-color: ${RAISED} !important;
    color: ${TEXT} !important;
  }
  body.dark-mode .error-fallback .btn-link {
    color: ${MUTED} !important;
  }
  body.dark-mode .error-fallback .btn-link:hover {
    color: ${TEXT} !important;
  }

  body.dark-mode .icon-button {
    box-shadow: none !important;

    [class$="-icon"] {
      rect {
        stroke: ${ACCENT} !important;
        fill: ${ACCENT} !important;
      }
    }
  }
`;

// ── Color utilities ───────────────────────────────────────────────────────────

// Lighten a color until it reaches the target contrast ratio against bg.
// Caps saturation at 0.75 to avoid over-saturated results.
export function toContrastColor(hex: string, bg: string = BG, targetContrast: number = 4.5): string {
  if (!hex) return TEXT;
  try {
    if (getContrast(hex, bg) >= targetContrast) return hex;
    const { hue, saturation } = parseToHsl(hex);
    const adjustedSat = Math.min(saturation, 0.75);
    for (let l = 0.45; l <= 0.96; l += 0.01) {
      const candidate = hslToColorString({ hue, saturation: adjustedSat, lightness: l });
      if (getContrast(candidate, bg) >= targetContrast) return candidate;
    }
    return '#ffffff';
  } catch {
    return TEXT;
  }
}

// Very dark tinted background for alert boxes (preserves hue, kills most lightness).
export function toAlertBg(hex: string): string {
  try {
    const { hue } = parseToHsl(hex);
    return hslToColorString({ hue, saturation: 0.15, lightness: 0.2 });
  } catch {
    return RAISED;
  }
}

export function toAlertBorder(hex: string): string {
  try {
    const { hue } = parseToHsl(hex);
    return hslToColorString({ hue, saturation: 0.3, lightness: 0.25 });
  } catch {
    return BORDER;
  }
}

// Convert a hex to "R, G, B" string for use in rgba().
function toRgbStr(hex: string): string {
  try {
    const { red, green, blue } = parseToRgb(hex);
    return `${red}, ${green}, ${blue}`;
  } catch {
    return '60, 110, 247';
  }
}

// ── Semantic CSS (fixed dark-mode palette) ───────────────────────────────────
// Dark mode no longer derives these from the org's light-theme brand colors
// (theme.primaryMain, etc.) — running an arbitrary org color through a contrast
// fixup produced muddy/clashing results. Use a fixed, curated set instead.

function buildSemanticCSS({
  primary,
  success,
  warning,
  info,
  danger,
}: {
  primary: string;
  success: string;
  warning: string;
  info: string;
  danger: string;
}): string {
  const onPrimary = readableColor(primary, '#ffffff', '#111111');
  const onSuccess = readableColor(success, '#ffffff', '#111111');
  const onWarning = readableColor(warning, '#ffffff', '#111111');
  const onInfo = readableColor(info, '#ffffff', '#111111');
  const onDanger = readableColor(danger, '#ffffff', '#111111');

  const primaryRgb = toRgbStr(primary || '#4e7184');
  const primaryHover = lighten(0.07, primary);

  return `
    body.dark-mode .nav-clock { color: ${TEXT} !important; }

    /* ── Semantic: tooltip border ───────────────────────────── */
    body.dark-mode .bs-tooltip-top .tooltip-inner,
    body.dark-mode .bs-tooltip-right .tooltip-inner,
    body.dark-mode .bs-tooltip-bottom .tooltip-inner,
    body.dark-mode .bs-tooltip-left .tooltip-inner,
    body.dark-mode .bs-tooltip-auto .tooltip-inner {
      border-color: ${primary} !important;
    }

    /* ── Semantic: links ─────────────────────────────────────── */
    body.dark-mode a { color: ${primary} !important; }
    body.dark-mode a:hover { color: ${lighten(0.1, primary)} !important; }

    /* ── Semantic: primary ───────────────────────────────────── */
    body.dark-mode .btn-primary {
      background-color: ${primary} !important;
      border-color: ${primary} !important;
      color: ${onPrimary} !important;
    }
    body.dark-mode .btn-primary:hover,
    body.dark-mode .btn-primary:focus {
      background-color: ${primaryHover} !important;
      border-color: ${primaryHover} !important;
    }
    body.dark-mode .btn-outline-primary {
      color: ${primary} !important;
      border-color: ${primary} !important;
    }
    body.dark-mode .btn-outline-primary:hover {
      background-color: ${primary} !important;
      color: ${onPrimary} !important;
    }
    body.dark-mode .text-primary { color: ${primary} !important; }
    body.dark-mode .alert-primary {
      background-color: ${toAlertBg(primary)} !important;
      border-color: ${toAlertBorder(primary)} !important;
      color: ${primary} !important;
    }
    body.dark-mode .badge.bg-primary,
    body.dark-mode .badge.badge-primary {
      background-color: ${primary} !important;
      color: ${onPrimary} !important;
    }
    body.dark-mode .react-datepicker__day--selected,
    body.dark-mode .react-datepicker__day--in-range,
    body.dark-mode .react-datepicker__day--in-selecting-range,
    body.dark-mode .react-datepicker__month-text--selected,
    body.dark-mode .react-datepicker__quarter-text--selected,
    body.dark-mode .react-datepicker__year-text--selected,
    body.dark-mode .react-datepicker__time-list-item--selected {
      background-color: ${primary} !important;
      color: ${onPrimary} !important;
    }
    body.dark-mode .react-datepicker__day--selected:hover {
      background-color: ${primaryHover} !important;
    }
    body.dark-mode .react-datepicker__year-option--selected_year,
    body.dark-mode .react-datepicker__year-option--selected {
      color: ${primary} !important;
    }
    /* ── Semantic: grouped tabs & pagination active ─────────── */
    body.dark-mode .form-grouped-tabs.nav .nav-link.active {
      background-color: ${ACCENT} !important;
      border-color: ${ACCENT} !important;
      color: #ffffff !important;
    }
    body.dark-mode .form-grouped-tabs.nav .nav-item.dropdown.active .dropdown-toggle {
      background-color: ${ACCENT} !important;
      border-color: ${ACCENT} !important;
      color: #ffffff !important;
    }
    body.dark-mode .page-item.active .page-link {
      background-color: ${ACCENT} !important;
      border-color: ${ACCENT} !important;
      color: #ffffff !important;
    }

    /* ── Semantic: tree-select ───────────────────────────────── */
    body.dark-mode .tree-select-menu .tree-node.tree-node-selectable:hover .tree-node-label {
      color: ${primary} !important;
    }
    body.dark-mode .tree-select-menu .tree-node.tree-node-selected {
      background: rgba(${primaryRgb}, 0.18) !important;
    }
    body.dark-mode .tree-select-menu .tree-node.tree-node-selected .tree-node-label {
      color: ${primary} !important;
    }
    body.dark-mode .tree-select-menu .tree-node-selected-indicator {
      background: rgba(${primaryRgb}, 0.15) !important;
      border-left-color: ${primary} !important;
      color: ${primary} !important;
    }
    body.dark-mode .tree-select-menu .tree-node-selected-indicator .tree-node-expand-btn {
      color: ${primary} !important;
    }

    /* ── Semantic: checkboxes & radios ──────────────────────── */
    body.dark-mode input[type='checkbox'],
    body.dark-mode input[type='radio'] {
      accent-color: ${primary};
    }

    /* ── Semantic: entity history & notes ───────────────────── */
    body.dark-mode .hist-user { color: ${primary} !important; }
    body.dark-mode .note-compose {
      border-bottom-color: ${primary} !important;
    }

    /* ── Semantic: danger ────────────────────────────────────── */
    body.dark-mode .text-danger { color: ${danger} !important; }
    body.dark-mode .btn-danger {
      background-color: ${danger} !important;
      border-color: ${danger} !important;
      color: ${onDanger} !important;
    }
    body.dark-mode .btn-danger:hover { background-color: ${lighten(0.07, danger)} !important; }
    body.dark-mode .alert-danger {
      background-color: ${toAlertBg(danger)} !important;
      border-color: ${toAlertBorder(danger)} !important;
      color: ${danger} !important;
    }
    body.dark-mode .badge.bg-danger,
    body.dark-mode .badge.badge-danger {
      background-color: ${danger} !important;
      color: ${onDanger} !important;
    }

    /* ── Semantic: warning ───────────────────────────────────── */
    body.dark-mode .text-warning { color: ${warning} !important; }
    body.dark-mode .btn-warning {
      background-color: ${warning} !important;
      border-color: ${warning} !important;
      color: ${onWarning} !important;
    }
    body.dark-mode .btn-warning:hover { background-color: ${lighten(0.07, warning)} !important; }
    body.dark-mode .alert-warning {
      background-color: ${toAlertBg(warning)} !important;
      border-color: ${toAlertBorder(warning)} !important;
      color: ${warning} !important;
    }
    body.dark-mode .badge.bg-warning,
    body.dark-mode .badge.badge-warning {
      background-color: ${warning} !important;
      color: ${onWarning} !important;
    }

    /* ── Semantic: success ───────────────────────────────────── */
    body.dark-mode .text-success { color: ${success} !important; }
    body.dark-mode .btn-success {
      background-color: ${success} !important;
      border-color: ${success} !important;
      color: ${onSuccess} !important;
    }
    body.dark-mode .btn-success:hover { background-color: ${lighten(0.07, success)} !important; }
    body.dark-mode .alert-success {
      background-color: ${toAlertBg(success)} !important;
      border-color: ${toAlertBorder(success)} !important;
      color: ${success} !important;
    }
    body.dark-mode .badge.bg-success,
    body.dark-mode .badge.badge-success {
      background-color: ${success} !important;
      color: ${onSuccess} !important;
    }

    /* ── Semantic: info ──────────────────────────────────────── */
    body.dark-mode .text-info { color: ${info} !important; }
    body.dark-mode .btn-info {
      background-color: ${info} !important;
      border-color: ${info} !important;
      color: ${onInfo} !important;
    }
    body.dark-mode .btn-info:hover { background-color: ${lighten(0.07, info)} !important; }
    body.dark-mode .alert-info {
      background-color: ${toAlertBg(info)} !important;
      border-color: ${toAlertBorder(info)} !important;
      color: ${info} !important;

      .text-muted {
        color: inherit !important;
      }
    }
    body.dark-mode .badge.bg-info,
    body.dark-mode .badge.badge-info {
      background-color: ${info} !important;
      color: ${onInfo} !important;
    }
  `;
}

// Computed once, at module load — the fixed palette never changes at runtime,
// so there's no need to recompute (or re-inject) this on every theme change.
const SEMANTIC_DARK_CSS = buildSemanticCSS({ primary: ACCENT, success: SUCCESS, warning: WARNING, info: INFO, danger: DANGER });

// ── Context & providers ───────────────────────────────────────────────────────

export const DarkModeContext = createContext<DarkModeContextValue>({ isDarkMode: false, toggleDarkMode: () => {} });

export function DarkModeProvider({ children }: { children: ReactNode }) {
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem(STORAGE_KEY) === 'true');

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'dark-mode-global-styles';
    style.textContent = DARK_CSS + SEMANTIC_DARK_CSS;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  useEffect(() => {
    document.body.classList.toggle(DARK_CLASS, isDarkMode);
    document.body.dataset.theme = isDarkMode ? 'dark' : 'light';
    localStorage.setItem(STORAGE_KEY, String(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = () => setIsDarkMode(prev => !prev);

  return (
    <DarkModeContext.Provider
      value={{
        isDarkMode,
        toggleDarkMode,
        colors: { BG, SURFACE, STRIPE, RAISED, BORDER, ROW_BORDER, TEXT, MUTED, INPUT, ACCENT, SUCCESS, WARNING, DANGER, INFO },
      }}
    >
      {children}
    </DarkModeContext.Provider>
  );
}

export function useDarkMode(): DarkModeContextValue {
  return useContext(DarkModeContext);
}
